// utils/getContrastTextColor.ts
type RGB = [number, number, number];
type BW = '#000000' | '#ffffff';

const contrastCache = new Map<string, BW>();

// Precompute sRGB -> linear RGB transform for 0..255
const gammaLUT: Float32Array = (() => {
  const lut = new Float32Array(256);
  for (let i = 0; i < 256; i++) {
    const c = i / 255;
    lut[i] = c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
  }
  return lut;
})();

const getContrastFromRGB = ([r8, g8, b8]: RGB): BW => {
  const r = gammaLUT[r8];
  const g = gammaLUT[g8];
  const b = gammaLUT[b8];
  const L = 0.2126 * r + 0.7152 * g + 0.0722 * b;

  // Contrast ratio with white vs black
  const cw = 1.05 / (L + 0.05);
  const cb = (L + 0.05) / 0.05;

  return cw > cb ? '#ffffff' : '#000000';
};

const parseColor = (color: string): RGB | null => {
  let m: RegExpMatchArray | null;

  // #RGB
  if ((m = color.match(/^#([0-9a-fA-F]{3})$/))) {
    const h = m[1];
    return [
      parseInt(h[0] + h[0], 16),
      parseInt(h[1] + h[1], 16),
      parseInt(h[2] + h[2], 16),
    ];
  }

  // #RRGGBB
  if ((m = color.match(/^#([0-9a-fA-F]{6})$/))) {
    const h = m[1];
    return [
      parseInt(h.slice(0, 2), 16),
      parseInt(h.slice(2, 4), 16),
      parseInt(h.slice(4, 6), 16),
    ];
  }

  // rgb(...) / rgba(...)
  if ((m = color.match(/^rgba?\(\s*([.\d]+)\s*,\s*([.\d]+)\s*,\s*([.\d]+)/))) {
    const r = Math.max(0, Math.min(255, Math.round(parseFloat(m[1]))));
    const g = Math.max(0, Math.min(255, Math.round(parseFloat(m[2]))));
    const b = Math.max(0, Math.min(255, Math.round(parseFloat(m[3]))));
    return [r, g, b];
  }

  return null;
};

/**
 * Fast memoized contrast chooser.
 * Cache key is exactly the provided string (no normalization).
 */
export const getContrastTextColor = (color: string): BW => {
  const key = color; // raw key for speed
  const cached = contrastCache.get(key);
  if (cached) return cached;

  const rgb = parseColor(color);
  const result: BW = rgb ? getContrastFromRGB(rgb) : '#000000';

  contrastCache.set(key, result);
  return result;
};

// Optional helpers
export const clearContrastCache = (): void => contrastCache.clear();
export const getContrastCacheSize = (): number => contrastCache.size;
